import { Autocomplete, debounce, TextField } from '@mui/material';
import { t } from 'i18next';
import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { AutogiroMandateResponse } from 'src/modules/invoice/models/autogiroMandateResponse';
import { AutogiroSearchFilter } from 'src/modules/invoice/models/autogiroSearchFilter';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { getNestedFormErrors } from 'src/shared/functions/getNestedFormErrors';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { SelectableItem } from 'src/shared/models/gui/selectableItem';
import { getAllAutogiroMandate } from 'src/shared/services/autogiroMandateService';

export const DonorAutogiroMandatePicker = () => {
  const isMounted = useIsMounted();
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<SelectableItem[]>();
  const [selectedValue, setSelectedValue] = useState<SelectableItem>();
  const {
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext();

  const formFieldname = 'autogiroMandate_id';

  let isError = false;
  const errorMessage = getNestedFormErrors(errors, formFieldname);
  if (errorMessage) {
    isError = true;
  }

  const fetch = useMemo(
    () =>
      debounce((inputVal: string) => {
        getAllAutogiroMandate({ filter: inputVal } as AutogiroSearchFilter)
          .then((result) => {
            if (isMounted()) {
              const newOptions = result.data.results.map((x: AutogiroMandateResponse) => ({
                label: `${x.firstName} ${x.lastName} (${x.payerNr || x.accountNr || x.personalIdentityNumber})`,
                value: x.autogiroMandate_id.toString(),
              }));
              setOptions(() => (selectedValue ? [selectedValue, ...newOptions] : newOptions));
            }
          })
          .catch((e) => {
            if (isMounted()) ConsoleHelper.error('Error fetching data:', e);
          });
      }, 400),
    [isMounted, selectedValue],
  );

  useEffect(() => {
    fetch(inputValue);
  }, [inputValue, fetch]);

  useEffect(() => {
    const currentValue = getValues(formFieldname);
    if (currentValue && options) {
      setSelectedValue(options.filter((x) => x.value.toString() === currentValue.toString())[0] || undefined);
    }
  }, [getValues, options]);

  return (
    <>
      {options && (
        <Autocomplete
          renderOption={(props, option) => {
            return (
              <li {...props} key={option.value}>
                {option.label}
              </li>
            );
          }}
          filterOptions={(x) => x}
          value={selectedValue || null}
          defaultValue={null}
          options={options}
          // onChange={(event, val) => {
          //   setValue('autogiroMandate_id', val?.value);
          //   setSelectedValue(val || undefined);
          // }}
          autoComplete
          includeInputInList
          filterSelectedOptions
          onChange={(event, newValue: SelectableItem | null) => {
            setOptions(newValue ? [newValue, ...options] : options);
            setValue('autogiroMandate_id', newValue?.value);
            setSelectedValue(newValue || undefined);
          }}
          onInputChange={(event, newInputValue) => {
            setInputValue(newInputValue);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              size='small'
              fullWidth
              label='Autogiro'
              error={isError}
              InputLabelProps={{ shrink: true }}
              helperText={isError ? t('common:validationMessages.required') : null}
            />
          )}
        />
      )}
    </>
  );
};
